import { useLocale } from "@/hooks/useLocale";
import { DynamicText } from "@/ui/DynamicText";
import { Col, Container, Row, Stack } from "react-bootstrap";
import { getAwards } from "@/functions/getAwards";
import { Heading } from "@/ui/Heading";
import { DynamicHtml } from "@/ui/DynamicHtml";
import clsx from "classnames";
import classes from "./styles.module.scss";
import { useEffect, useState } from "react";
import { useMediaQuery } from "@react-hook/media-query";
import { Carousel } from "@/ui/Carousel";
import { chunk } from "lodash";

const AWARD_LOGO_HEIGHT = 150;

/**
 * Builds the CDN sources for an award logo at the height it is actually displayed.
 *
 * Award images come from the CMS as unresized originals, so they are requested through
 * the CDN's height-only variant path, which preserves each logo's aspect ratio and
 * returns webp. Returns null for any URL the CDN cannot resize, leaving it untouched.
 *
 * @param {string} [image]
 * @returns {{src: string, srcSet: string}|null}
 */
const getAwardLogoSources = (image) => {
  const id = image?.match(/^https:\/\/cdn\.sourceflow\.co\.uk\/([A-Za-z0-9_-]+)$/)?.[1];

  if (!id) {
    return null;
  }

  const variant = (height) => `https://cdn.sourceflow.co.uk/variant/x${height}/${id}`;

  return {
    src: variant(AWARD_LOGO_HEIGHT),
    srcSet: `${variant(AWARD_LOGO_HEIGHT)} 1x, ${variant(AWARD_LOGO_HEIGHT * 2)} 2x`,
  };
};

/**
 * Awards carousel: renders the CMS award logos with their captions.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const AwardsSection = ({ className, variant, indicators }) => {
  const lg = useMediaQuery("only screen and (min-width: 992px)");
  const md = useMediaQuery("only screen and (min-width: 768px)");
  const [visibleCount, setVisibleCount] = useState(3);
  const awards = getAwards();
  const locale = useLocale();

  useEffect(() => {
    setVisibleCount(lg ? 4 : md ? 3 : 1);
  }, [lg, md]);

  return (
    <div className={clsx(className, classes.awards, `variant-${variant}`)}>
      <Container className="px-2 px-md-3">
        <Heading className="mb-4" variant={variant}>
          <DynamicText path={`section.awards.title.${locale}`} className="h2" tag="h3" />
        </Heading>
        <DynamicHtml path={`section.awards.text.${locale}`} className="pb-3" />
        <Carousel
          indicators={indicators}
          items={chunk(awards, visibleCount).map((i, k) => (
            <Row key={k}>
              {i.map((award) => {
                const logo = getAwardLogoSources(award.image);

                return (
                  <Col key={award.id} xs={12} md={12 / visibleCount}>
                    <div key={k} className="p-1 p-md-3 d-flex flex-column justify-content-around">
                      <figure>
                        <div
                          className="d-flex align-items-end justify-content-center align-items-center pb-md-2"
                          style={{ height: AWARD_LOGO_HEIGHT }}
                        >
                          <img
                            src={logo?.src ?? award.image}
                            srcSet={logo?.srcSet}
                            alt=""
                            className="mw-100 mh-100"
                          />
                        </div>
                        <figcaption className="d-block text-center fw-semibold">{award[`title_${locale}`]}</figcaption>
                      </figure>
                    </div>
                  </Col>
                );
              })}
            </Row>
          ))}
        />
      </Container>
    </div>
  );
};

AwardsSection.defaultProps = {
  className: "py-4 py-md-5 mb-4 mb-md-5",
  variant: "primary",
  indicators: false,
};
